import {
  type AnswerRepliesSetting,
  answerRepliesSettingSchema,
} from "@/features/pulse-surveys/schemas/answer-replies-setting.schema";
import {
  type DeliveryReplyEligibility,
  deliveryReplyEligibilitySchema,
  type MemberAnswerReplies,
  memberAnswerRepliesSchema,
} from "@/features/pulse-surveys/schemas/member-answer-replies.schema";
import {
  type MyTeamAudience,
  myTeamAudienceSchema,
} from "@/features/pulse-surveys/schemas/my-team-audience.schema";
import {
  type AdminSurveyType,
  type CreatePulseSurvey,
  type ListPendingDeliveriesResponse,
  type ListPulseSurveysResponse,
  listPendingDeliveriesResponseSchema,
  listPulseSurveysResponseSchema,
  type OkResponse,
  type OwnHistoryResponse,
  okResponseSchema,
  ownHistoryResponseSchema,
  type PulseSurveyRow,
  type PulseSurveyStatus,
  pulseSurveyRowSchema,
  type SubmitResponse,
  type SubmitResponseResult,
  submitResponseResultSchema,
  type UpdatePulseSurvey,
} from "@/features/pulse-surveys/schemas/pulse-survey.schema";
import {
  type SelfCheckInOverview,
  type StartSelfCheckInRequest,
  type StartSelfCheckInResponse,
  selfCheckInOverviewSchema,
  startSelfCheckInResponseSchema,
} from "@/features/pulse-surveys/schemas/self-check-in.schema";
import {
  type UpdateWorkspaceHealthConfig,
  type WorkspaceHealthConfig,
  workspaceHealthConfigSchema,
} from "@/features/pulse-surveys/schemas/workspace-health-config.schema";
import { apiCall } from "@/shared/lib/api-call";
import { apiPaths } from "@/shared/lib/api-paths";

/// HTTP boundary for M8 survey reads/writes. Per webui T3 / FE-SEC6
/// every response is Zod-parsed via the shared `apiCall` helper. The
/// `/health-vocabulary/*` reads moved to
/// `@/shared/pulse/health/health-vocabulary.api` in the C1 Phase B
/// refactor, they aren't survey-specific.

export async function getWorkspaceHealthConfig(
  workspaceId: string,
): Promise<WorkspaceHealthConfig> {
  return apiCall<WorkspaceHealthConfig>(
    apiPaths.healthConfig(workspaceId),
    { method: "GET" },
    workspaceHealthConfigSchema,
  );
}

export async function updateWorkspaceHealthConfig(
  workspaceId: string,
  body: UpdateWorkspaceHealthConfig,
): Promise<WorkspaceHealthConfig> {
  return apiCall<WorkspaceHealthConfig>(
    apiPaths.healthConfig(workspaceId),
    { method: "PATCH", body: JSON.stringify(body) },
    workspaceHealthConfigSchema,
  );
}

export async function getAnswerRepliesSetting(workspaceId: string): Promise<AnswerRepliesSetting> {
  return apiCall<AnswerRepliesSetting>(
    apiPaths.pulseSurveys.answerRepliesSetting(workspaceId),
    { method: "GET" },
    answerRepliesSettingSchema,
  );
}

export async function updateAnswerRepliesSetting(
  workspaceId: string,
  enabled: boolean,
): Promise<AnswerRepliesSetting> {
  return apiCall<AnswerRepliesSetting>(
    apiPaths.pulseSurveys.answerRepliesSetting(workspaceId),
    { method: "PATCH", body: JSON.stringify({ enabled }) },
    answerRepliesSettingSchema,
  );
}

export async function getMemberAnswerReplies(workspaceId: string): Promise<MemberAnswerReplies> {
  return apiCall<MemberAnswerReplies>(
    apiPaths.pulseSurveys.meAnswerReplies(workspaceId),
    { method: "GET" },
    memberAnswerRepliesSchema,
  );
}

export async function getDeliveryReplyEligibility(
  workspaceId: string,
  deliveryId: string,
): Promise<DeliveryReplyEligibility> {
  return apiCall<DeliveryReplyEligibility>(
    apiPaths.pulseSurveys.meDeliveryAnswerReplies(workspaceId, deliveryId),
    { method: "GET" },
    deliveryReplyEligibilitySchema,
  );
}

// -- Pulse survey CRUD ------------------------------------------------------

export interface ListPulseSurveysQuery {
  status?: PulseSurveyStatus;
  surveyType?: AdminSurveyType;
  cursor?: string;
  limit?: number;
}

function toSurveysQs(query: ListPulseSurveysQuery): string {
  const params = new URLSearchParams();
  if (query.status) params.set("status", query.status);
  if (query.surveyType) params.set("surveyType", query.surveyType);
  if (query.cursor) params.set("cursor", query.cursor);
  if (query.limit !== undefined) params.set("limit", String(query.limit));
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

export async function listPulseSurveys(
  workspaceId: string,
  query: ListPulseSurveysQuery = {},
): Promise<ListPulseSurveysResponse> {
  return apiCall<ListPulseSurveysResponse>(
    `${apiPaths.pulseSurveys.list(workspaceId)}${toSurveysQs(query)}`,
    { method: "GET" },
    listPulseSurveysResponseSchema,
  );
}

/// Owner-scoped list (GET /pulse-surveys/mine): the caller's own custom
/// surveys. This is the manager list surface, since managers do not hold
/// PULSE_SURVEY_LIST_WORKSPACE and the workspace list 403s for them.
export async function listMyCreatedPulseSurveys(
  workspaceId: string,
  query: ListPulseSurveysQuery = {},
): Promise<ListPulseSurveysResponse> {
  return apiCall<ListPulseSurveysResponse>(
    `${apiPaths.pulseSurveys.mine(workspaceId)}${toSurveysQs(query)}`,
    { method: "GET" },
    listPulseSurveysResponseSchema,
  );
}

/// The caller's own reporting subtree + the workspace anonymity threshold,
/// powering the manager audience picker (GET /pulse-surveys/my-team).
export async function getMyTeamAudience(workspaceId: string): Promise<MyTeamAudience> {
  return apiCall<MyTeamAudience>(
    apiPaths.pulseSurveys.myTeam(workspaceId),
    { method: "GET" },
    myTeamAudienceSchema,
  );
}

export async function getPulseSurvey(
  workspaceId: string,
  surveyId: string,
): Promise<PulseSurveyRow> {
  return apiCall<PulseSurveyRow>(
    apiPaths.pulseSurveys.detail(workspaceId, surveyId),
    { method: "GET" },
    pulseSurveyRowSchema,
  );
}

export async function createPulseSurvey(
  workspaceId: string,
  body: CreatePulseSurvey,
): Promise<PulseSurveyRow> {
  return apiCall<PulseSurveyRow>(
    apiPaths.pulseSurveys.list(workspaceId),
    { method: "POST", body: JSON.stringify(body) },
    pulseSurveyRowSchema,
  );
}

/// Resend a one-shot custom survey: shore moves the schedule to `fireAt`
/// (default now), reactivates if paused, and the dispatcher fires a second
/// run of the same survey.
export async function resendPulseSurvey(
  workspaceId: string,
  surveyId: string,
  body: { fireAt?: string },
): Promise<PulseSurveyRow> {
  return apiCall<PulseSurveyRow>(
    apiPaths.pulseSurveys.resend(workspaceId, surveyId),
    { method: "POST", body: JSON.stringify(body) },
    pulseSurveyRowSchema,
  );
}

export async function updatePulseSurvey(
  workspaceId: string,
  surveyId: string,
  body: UpdatePulseSurvey,
): Promise<PulseSurveyRow> {
  return apiCall<PulseSurveyRow>(
    apiPaths.pulseSurveys.detail(workspaceId, surveyId),
    { method: "PATCH", body: JSON.stringify(body) },
    pulseSurveyRowSchema,
  );
}

export async function deletePulseSurvey(
  workspaceId: string,
  surveyId: string,
): Promise<OkResponse> {
  return apiCall<OkResponse>(
    apiPaths.pulseSurveys.detail(workspaceId, surveyId),
    { method: "DELETE" },
    okResponseSchema,
  );
}

/// Slice 6, member-response surface.

export async function listMyPendingPulseSurveys(
  workspaceId: string,
): Promise<ListPendingDeliveriesResponse> {
  return apiCall<ListPendingDeliveriesResponse>(
    apiPaths.pulseSurveys.mePending(workspaceId),
    { method: "GET" },
    listPendingDeliveriesResponseSchema,
  );
}

export async function submitPulseSurveyResponse(
  workspaceId: string,
  deliveryId: string,
  body: SubmitResponse,
): Promise<SubmitResponseResult> {
  return apiCall<SubmitResponseResult>(
    apiPaths.pulseSurveys.meDelivery(workspaceId, deliveryId),
    { method: "POST", body: JSON.stringify(body) },
    submitResponseResultSchema,
  );
}

export interface ListOwnHistoryQuery {
  cursor?: string;
  limit?: number;
}

export async function listMyPulseSurveyHistory(
  workspaceId: string,
  query: ListOwnHistoryQuery = {},
): Promise<OwnHistoryResponse> {
  const params = new URLSearchParams();
  if (query.cursor) params.set("cursor", query.cursor);
  if (query.limit !== undefined) params.set("limit", String(query.limit));
  const qs = params.toString();
  return apiCall<OwnHistoryResponse>(
    `${apiPaths.pulseSurveys.meHistory(workspaceId)}${qs ? `?${qs}` : ""}`,
    { method: "GET" },
    ownHistoryResponseSchema,
  );
}

// --- M8 slice 8, run management -----------------------------------------

import {
  type AiPulseOverview,
  aiPulseOverviewSchema,
  type CancelRunResponse,
  cancelRunResponseSchema,
  type ListRunsResponse,
  listRunsResponseSchema,
  type PulseSurveyRunDetail,
  pulseSurveyRunDetailSchema,
} from "@/features/pulse-surveys/schemas/pulse-survey-run.schema";

export interface ListRunsQuery {
  cursor?: string;
  limit?: number;
}

export async function listPulseSurveyRuns(
  workspaceId: string,
  surveyId: string,
  query: ListRunsQuery = {},
): Promise<ListRunsResponse> {
  const params = new URLSearchParams();
  if (query.cursor) params.set("cursor", query.cursor);
  if (query.limit !== undefined) params.set("limit", String(query.limit));
  const qs = params.toString();
  return apiCall<ListRunsResponse>(
    `${apiPaths.pulseSurveys.runs(workspaceId, surveyId)}${qs ? `?${qs}` : ""}`,
    { method: "GET" },
    listRunsResponseSchema,
  );
}

export async function cancelPulseSurveyRun(
  workspaceId: string,
  surveyId: string,
  runId: string,
): Promise<CancelRunResponse> {
  return apiCall<CancelRunResponse>(
    apiPaths.pulseSurveys.runCancel(workspaceId, surveyId, runId),
    { method: "POST", body: "{}" },
    cancelRunResponseSchema,
  );
}

// --- M8 slice 9, AI test-run --------------------------------------------

import {
  type AiTestRunResponse,
  aiTestRunResponseSchema,
} from "@/features/pulse-surveys/schemas/ai-test-run.schema";

export interface AiTestRunBody {
  mockEventPayload?: Record<string, unknown>;
}

export async function runAiTestForSurvey(
  workspaceId: string,
  surveyId: string,
  body: AiTestRunBody = {},
): Promise<AiTestRunResponse> {
  return apiCall<AiTestRunResponse>(
    apiPaths.pulseSurveys.aiTestRun(workspaceId, surveyId),
    { method: "POST", body: JSON.stringify(body) },
    aiTestRunResponseSchema,
  );
}

// Change 5 (2026-05-21): safety queue removed.

// --- Survey-types UI, self check-in + AI Pulse + identified answers ------

export async function getSelfCheckInOverview(workspaceId: string): Promise<SelfCheckInOverview> {
  return apiCall<SelfCheckInOverview>(
    apiPaths.pulseSurveys.meSelfCheckIn(workspaceId),
    { method: "GET" },
    selfCheckInOverviewSchema,
  );
}

export async function startSelfCheckIn(
  workspaceId: string,
  body: StartSelfCheckInRequest,
): Promise<StartSelfCheckInResponse> {
  return apiCall<StartSelfCheckInResponse>(
    apiPaths.pulseSurveys.meSelfCheckIn(workspaceId),
    { method: "POST", body: JSON.stringify(body) },
    startSelfCheckInResponseSchema,
  );
}

export async function getAiPulseOverview(workspaceId: string): Promise<AiPulseOverview> {
  return apiCall<AiPulseOverview>(
    apiPaths.pulseSurveys.aiPulse(workspaceId),
    { method: "GET" },
    aiPulseOverviewSchema,
  );
}

export async function getPulseSurveyRunDetail(
  workspaceId: string,
  surveyId: string,
  runId: string,
): Promise<PulseSurveyRunDetail> {
  return apiCall<PulseSurveyRunDetail>(
    apiPaths.pulseSurveys.runDetail(workspaceId, surveyId, runId),
    { method: "GET" },
    pulseSurveyRunDetailSchema,
  );
}
